import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import PrescriptionPreview from "@/components/PrescriptionPreview";
import {
  ArrowLeft,
  FileText,
  Pill,
  FlaskConical,
  FileBadge2,
  Receipt,
  Share2,
  Activity,
  ChevronRight,
  User,
  Camera,
  Phone,
  MapPin,
  CalendarDays,
  ClipboardList,
  Stethoscope,
  Paperclip,
  History,
  AlertTriangle,
  X,
} from "lucide-react";

const MM_TO_PX = 96 / 25.4;
const PAPER_MM = {
  A4: [210, 297],
  Letter: [215.9, 279.4],
  Half: [139.7, 215.9],
};

// Paper size in px straight from the hospital's print settings. Deliberately not
// measured from the DOM: a measured width is only correct once the sheet has
// laid out, and reading it back while a transform is applied is how the earlier
// version fed its own output into its input.
function paperPx(settings) {
  const s = settings || {};
  const [wmm, hmm] =
    s.paper_size === "Custom"
      ? [Number(s.custom_width) || 210, Number(s.custom_height) || 297]
      : PAPER_MM[s.paper_size] || PAPER_MM.A4;
  return { w: wmm * MM_TO_PX, h: hmm * MM_TO_PX };
}

// Read-only, full-screen look at a prescription. On a phone a doctor is
// checking what they wrote, not printing it — so this shows the sheet and
// nothing else: no action bar, no page picker, no print/PDF. Just the page,
// whole, and a way out.
//
// The sheet is real paper laid out in mm (A4, or this fleet's 148x210mm
// clinics), i.e. 530-795px wide, so it has to be scaled down. Fit BOTH axes so
// a full page is on screen at once rather than running off the bottom. The
// factor is measured because paper_size can be "Custom" (width arrives as an
// inline style) and CSS calc() cannot divide one length by another.
function RxImageViewer({ rxId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState("");

  const scrollRef = useRef(null);
  const hostRef = useRef(null);
  const innerRef = useRef(null);
  const [fit, setFit] = useState({ k: 1, h: null, tx: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get(`/prescriptions/${rxId}`),
          api.get("/print-settings"),
        ]);
        if (!alive) return;
        setDoc(p.data);
        setSettings(s.data || {});
      } catch (e) {
        if (alive) setErr(formatErr(e?.response?.data?.detail));
      }
    })();
    return () => {
      alive = false;
    };
  }, [rxId]);

  useEffect(() => {
    if (!doc) return;

    const apply = (next) =>
      setFit((prev) =>
        Math.abs(prev.k - next.k) < 0.001 &&
        prev.h === next.h &&
        Math.abs(prev.tx - next.tx) < 1
          ? prev
          : next
      );

    const measure = () => {
      const scroller = scrollRef.current;
      const host = hostRef.current;
      const inner = innerRef.current;
      if (!scroller || !host || !inner) return;

      const { w: pw, h: ph } = paperPx(settings);
      const aw = host.clientWidth;
      const ah = scroller.clientHeight - 16; // the scroller's own padding
      const contentH = inner.offsetHeight;

      // Anything degenerate — still laying out, fonts or the letterhead image
      // not in yet — means show the sheet at 1:1 and let it scroll. Falling back
      // to "unscaled and scrollable" can look clumsy; falling back to a computed
      // height can render a blank strip, which is worse than clumsy.
      if (aw < 50 || ah < 50 || contentH < 50) {
        apply({ k: 1, h: null, tx: 0 });
        return;
      }

      const k = Math.min(1, aw / pw, ah / ph);
      const tx = Math.max(0, (aw - pw * k) / 2);
      const h = k < 1 ? Math.ceil(contentH * k) : null;
      apply({ k, h, tx });
    };

    // Measure across a few frames: fonts, the letterhead and the signature image
    // all land after first paint and each one changes the height.
    const timers = [50, 200, 600, 1200].map((ms) => setTimeout(measure, ms));
    const ro = new ResizeObserver(measure);
    if (innerRef.current) ro.observe(innerRef.current);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener("resize", measure);

    return () => {
      timers.forEach(clearTimeout);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [doc, settings]);

  // Hardware/browser back should close the viewer, not leave the chart.
  useEffect(() => {
    window.history.pushState({ rxViewer: true }, "");
    const pop = () => onClose();
    window.addEventListener("popstate", pop);
    return () => {
      window.removeEventListener("popstate", pop);
      if (window.history.state?.rxViewer) window.history.back();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900 flex flex-col" data-testid="mobile-rx-viewer">
      <div className="flex items-center justify-between gap-3 px-4 py-3 shrink-0">
        <div className="text-white font-bold truncate">
          {doc?.prescription_number || "Prescription"}
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="mobile-rx-viewer-close"
          className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center shrink-0 active:scale-95"
        >
          <X size={20} />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-2">
        {err ? (
          <div className="text-center text-slate-300 py-16">{err}</div>
        ) : !doc ? (
          <div className="text-center text-slate-400 py-16">Loading…</div>
        ) : (
          <div
            ref={hostRef}
            className="rx-vfit-host"
            style={fit.h ? { height: fit.h } : undefined}
          >
            <div
              ref={innerRef}
              className="rx-vfit"
              style={
                fit.k < 1
                  ? { transform: `translateX(${fit.tx}px) scale(${fit.k})` }
                  : undefined
              }
              data-testid="mobile-rx-fit"
            >
              <PrescriptionPreview prescription={doc} settings={settings} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CardButton({ icon: Icon, label, sub, tone = "blue", onClick }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    purple: "bg-purple-50 text-purple-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl bg-white border border-slate-200 p-4 text-left flex items-center justify-between active:scale-[0.99] shadow-sm"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tones[tone] || tones.blue}`}>
          <Icon size={21} />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-slate-900">{label}</div>
          {sub ? <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div> : null}
        </div>
      </div>
      <ChevronRight size={18} className="text-slate-400 shrink-0" />
    </button>
  );
}

function fmtDate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

export default function DoctorMobilePatient() {
  const { id } = useParams();
  const nav = useNavigate();

  const [patient, setPatient] = useState(null);
  const [latestVitals, setLatestVitals] = useState(null);
  const [latestRecord, setLatestRecord] = useState(null);
  const [counts, setCounts] = useState({
    records: 0,
    prescriptions: 0,
    labs: 0,
    certificates: 0,
    billing: 0,
    referrals: 0,
    vitals: 0,
    notes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [rxList, setRxList] = useState([]);   // this patient's prescriptions
  const [rxSheet, setRxSheet] = useState(false); // prescriptions bottom-sheet open
  const [rxView, setRxView] = useState(null);    // prescription id open in the viewer

  const uploadPhoto = async (fileObj) => {
    if (!fileObj) return;
    if (!fileObj.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post(`/patients/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPatient((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Patient photo saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, records, rx, labs, certs, refs, vitals, tx, notes] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patient-records/patient/${id}`).catch(() => ({ data: [] })),
        api.get(`/prescriptions?patient_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/lab-results`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/medical-certificates`).catch(() => ({ data: [] })),
        api.get(`/referrals?patient_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/vitals`).catch(() => ({ data: [] })),
        api.get(`/pos/transactions`, { params: { scope: "all" } }).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/clinical-notes`).catch(() => ({ data: [] })),
      ]);

      const allRx = Array.isArray(rx.data) ? rx.data : [];
      const allRefs = Array.isArray(refs.data) ? refs.data : [];
      const allTx = Array.isArray(tx.data) ? tx.data : [];
      const recordList = Array.isArray(records.data) ? records.data : [];
      const vitalsList = Array.isArray(vitals.data) ? vitals.data : [];
      const notesList = Array.isArray(notes.data) ? notes.data : [];

      setPatient(p.data);
      setLatestVitals(vitalsList[0] || null);
      setLatestRecord(recordList[0] || null);
      setRxList(allRx);

      setCounts({
        records: recordList.length,
        prescriptions: allRx.filter((x) => x.patient_id === id && x.status !== "deleted").length,
        labs: Array.isArray(labs.data) ? labs.data.length : 0,
        certificates: Array.isArray(certs.data) ? certs.data.length : 0,
        billing: allTx.filter((x) => x.patient_id === id).length,
        referrals: allRefs.filter((x) => x.patient_id === id).length,
        vitals: vitalsList.length,
        notes: notesList.length,
      });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 p-5">
        <div className="text-slate-500">Loading patient...</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-slate-100 p-5">
        <button onClick={() => nav(-1)} className="mb-4 text-blue-700 font-bold">Back</button>
        <div className="text-slate-500">Patient not found.</div>
      </div>
    );
  }

  const demographics = [
    patient.age ? `${patient.age} y/o` : "",
    patient.gender || "",
    patient.patient_number || "",
  ].filter(Boolean).join(" · ");

  const v = latestVitals?.vitals || {};

  return (
    <MobileShell>
      {/* Page header (back + name) */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center active:scale-95 transition shrink-0"
          aria-label="Back"
          data-testid="patient-back"
        >
          <ArrowLeft size={20} className="text-slate-700" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-xl font-extrabold text-slate-900 leading-tight truncate" style={{ fontFamily: "Work Sans" }}>
            {patient.full_name}
          </div>
          <div className="text-sm text-slate-500">Patient Summary</div>
        </div>
      </div>

      <div className="p-4">
        <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white p-5 mb-4 shadow-lg">
          <label className="relative w-16 h-16 rounded-2xl bg-white/15 overflow-hidden flex items-center justify-center mb-3 active:scale-95 transition cursor-pointer" data-testid="patient-photo">
            {patient.photo_url ? (
              <img src={patient.photo_url} alt={patient.full_name || "patient"} className="w-full h-full object-cover" />
            ) : (
              <User size={26} />
            )}
            <span className="absolute bottom-0 inset-x-0 bg-black/40 flex items-center justify-center py-0.5">
              <Camera size={13} className="text-white" />
            </span>
            <input type="file" accept="image/*" className="hidden" disabled={photoBusy} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="patient-photo-input" />
          </label>

          <div className="text-2xl font-black leading-tight">{patient.full_name}</div>
          <div className="text-sm text-blue-50 mt-1">{demographics || "No demographics"}</div>

          <div className="grid grid-cols-1 gap-2 mt-4 text-sm">
            {patient.contact_number ? (
              <div className="flex items-center gap-2 text-blue-50">
                <Phone size={14} />
                {patient.contact_number}
              </div>
            ) : null}

            {patient.address ? (
              <div className="flex items-center gap-2 text-blue-50">
                <MapPin size={14} />
                {patient.address}
              </div>
            ) : null}

            {patient.birthdate ? (
              <div className="flex items-center gap-2 text-blue-50">
                <CalendarDays size={14} />
                DOB: {fmtDate(patient.birthdate)}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-2xl bg-white p-4 border border-slate-200 shadow-sm">
            <div className="text-xs text-slate-500">Vitals</div>
            <div className="text-lg font-black text-slate-900 mt-1">
              {v.blood_pressure || "—"}
            </div>
            <div className="text-xs text-slate-400 mt-1">Latest BP</div>
          </div>

          <div className="rounded-2xl bg-white p-4 border border-slate-200 shadow-sm">
            <div className="text-xs text-slate-500">Records</div>
            <div className="text-lg font-black text-slate-900 mt-1">{counts.records}</div>
            <div className="text-xs text-slate-400 mt-1">Total entries</div>
          </div>
        </div>

        {latestRecord ? (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-amber-900">Latest Record</div>
                <div className="text-sm text-amber-800 mt-1 truncate">{latestRecord.title || latestRecord.type}</div>
                <div className="text-xs text-amber-700 mt-1">{fmtDate(latestRecord.date || latestRecord.created_at)}</div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Today&apos;s Work</div>
        </div>

        <div className="space-y-3">
          <CardButton
            icon={Activity}
            label="Vitals"
            sub={`${counts.vitals} entries`}
            tone="emerald"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={Stethoscope}
            label="SOAP / Clinical Notes"
            sub={`${counts.notes} notes`}
            tone="blue"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={Pill}
            label="Prescription"
            sub={`${counts.prescriptions} prescriptions · tap to view`}
            tone="emerald"
            onClick={() => setRxSheet(true)}
          />

          <CardButton
            icon={FileBadge2}
            label="Medical Certificate"
            sub={`${counts.certificates} certificates`}
            tone="purple"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={Share2}
            label="Referral"
            sub={`${counts.referrals} referrals`}
            tone="amber"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={FlaskConical}
            label="Laboratory"
            sub={`${counts.labs} lab records`}
            tone="red"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={Receipt}
            label="Billing / POS"
            sub={`${counts.billing} transactions`}
            tone="slate"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={Paperclip}
            label="Attachments"
            sub="Documents, files, images"
            tone="slate"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={History}
            label="Timeline / History"
            sub={`${counts.records + counts.prescriptions + counts.labs + counts.certificates} clinical events`}
            tone="blue"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />

          <CardButton
            icon={FileText}
            label="Open Full Desktop Chart"
            sub="Use this when more detail is needed"
            tone="slate"
            onClick={() => nav(`/patients/${id}?full=1`)}
          />
        </div>
      </div>

      {rxSheet ? (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" data-testid="mobile-rx-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setRxSheet(false)} />
          <div className="relative bg-white rounded-t-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="font-black text-slate-900 text-lg flex items-center gap-2">
                <Pill size={18} className="text-emerald-600" /> Prescriptions
              </div>
              <button onClick={() => setRxSheet(false)} className="text-slate-500 text-2xl leading-none px-2" aria-label="Close">×</button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {rxList.length === 0 ? (
                <div className="text-center text-slate-400 py-10">No prescriptions yet.</div>
              ) : rxList.map((r) => {
                const meds = Array.isArray(r.medicines) ? r.medicines : [];
                const summary = r.diagnosis || meds.map((m) => m.medicine_name).filter(Boolean).slice(0, 3).join(", ") || "—";
                return (
                  <button
                    key={r.id}
                    onClick={() => setRxView(r.id)}
                    className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 active:scale-[0.99]"
                    data-testid={`mobile-rx-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-slate-900">{r.prescription_number || "Rx"}</div>
                      <div className="text-xs text-slate-500 shrink-0">{fmtDate(r.date || r.created_at)}</div>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 truncate">{summary}</div>
                    <div className="text-xs text-emerald-700 mt-2 flex items-center gap-1">View <ChevronRight size={14} /></div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {rxView ? (
        <RxImageViewer rxId={rxView} onClose={() => setRxView(null)} />
      ) : null}
    </MobileShell>
  );
}
